import { useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { SelectField } from '@/components/ui/SelectField'
import { TextField } from '@/components/ui/TextField'
import type { ProjectRecord } from '@/data/useProjects'
import type { UserRecord } from '@/data/useUsers'
import { createProject, updateProject, type NewProjectInput } from '@/data/projectApi'
import { AddUserModal } from '@/features/admin/users/AddUserModal'
import { errorMessage } from '@/lib/errors'
import { INCOTERMS } from '@/types/project'

interface ProjectFormModalProps {
  project: ProjectRecord | null
  suppliers: UserRecord[]
  clients: UserRecord[]
  createdBy: string
  onClose: () => void
  onSaved: (name: string) => void
}

function initialValues(project: ProjectRecord | null): NewProjectInput {
  if (project) {
    return {
      name: project.name,
      contractNumber: project.contractNumber,
      productName: project.productName,
      productQuantity: project.productQuantity,
      incoterms: project.incoterms,
      deliveryLocation: project.deliveryLocation,
      originLocation: project.originLocation ?? '',
      supplierUid: project.supplierUid,
      supplierName: project.supplierName,
      clientUid: project.clientUid,
      clientName: project.clientName,
    }
  }
  return {
    name: '',
    contractNumber: '',
    productName: '',
    productQuantity: '',
    incoterms: 'FCA',
    deliveryLocation: '',
    originLocation: '',
    supplierUid: '',
    supplierName: '',
    clientUid: '',
    clientName: '',
  }
}

// Conditionally mounted (see ProjectsPage), so state initialises from props once.
export function ProjectFormModal({
  project,
  suppliers,
  clients,
  createdBy,
  onClose,
  onSaved,
}: ProjectFormModalProps) {
  const [values, setValues] = useState<NewProjectInput>(() => initialValues(project))
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [addingRole, setAddingRole] = useState<'supplier' | 'client' | null>(null)

  function set<K extends keyof NewProjectInput>(key: K, value: NewProjectInput[K]) {
    setValues((current) => ({ ...current, [key]: value }))
  }

  function selectSupplier(uid: string) {
    const supplier = suppliers.find((candidate) => candidate.id === uid)
    setValues((current) => ({ ...current, supplierUid: uid, supplierName: supplier?.name ?? '' }))
  }

  function selectClient(uid: string) {
    const client = clients.find((candidate) => candidate.id === uid)
    setValues((current) => ({ ...current, clientUid: uid, clientName: client?.name ?? '' }))
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const quantity = values.productQuantity.trim()
    const required =
      values.name.trim() &&
      values.contractNumber.trim() &&
      values.productName.trim() &&
      quantity &&
      values.deliveryLocation.trim() &&
      values.supplierUid &&
      values.clientUid
    if (!required) {
      setError('Please complete all required fields, including supplier and client.')
      return
    }
    if (!/^\d+(\.\d+)?$/.test(quantity) || Number(quantity) <= 0) {
      setError('Product quantity must be a number greater than zero.')
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      if (project) {
        await updateProject(project.id, values)
      } else {
        await createProject(values, createdBy)
      }
      onSaved(values.name.trim())
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Modal open title={project ? 'Edit project' : 'Add delivery project'} onClose={onClose}>
        <form
          className="max-h-[70vh] space-y-4 overflow-y-auto pr-1"
          onSubmit={handleSubmit}
          noValidate
        >
          <TextField
            id="project-name"
            label="Project name"
            value={values.name}
            onChange={(event) => set('name', event.target.value)}
          />
          <TextField
            id="project-contract"
            label="Contract number"
            value={values.contractNumber}
            onChange={(event) => set('contractNumber', event.target.value)}
          />
          <TextField
            id="project-product"
            label="Product name"
            value={values.productName}
            onChange={(event) => set('productName', event.target.value)}
          />
          <TextField
            id="project-quantity"
            label="Product quantity"
            type="number"
            min="0"
            step="any"
            inputMode="decimal"
            value={values.productQuantity}
            onChange={(event) => set('productQuantity', event.target.value)}
            placeholder="e.g. 500"
          />
          <SelectField
            id="project-incoterms"
            label="Incoterms"
            value={values.incoterms}
            onChange={(event) =>
              set('incoterms', event.target.value as NewProjectInput['incoterms'])
            }
          >
            {INCOTERMS.map((term) => (
              <option key={term} value={term}>
                {term}
              </option>
            ))}
          </SelectField>
          <TextField
            id="project-delivery"
            label="Delivery location"
            value={values.deliveryLocation}
            onChange={(event) => set('deliveryLocation', event.target.value)}
          />
          <TextField
            id="project-origin"
            label="Origin / loading location (optional)"
            value={values.originLocation}
            onChange={(event) => set('originLocation', event.target.value)}
          />
          <div>
            <SelectField
              id="project-supplier"
              label="Supplier"
              value={values.supplierUid}
              onChange={(event) => selectSupplier(event.target.value)}
            >
              <option value="">Select supplier…</option>
              {suppliers.map((supplier) => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </option>
              ))}
            </SelectField>
            <button
              type="button"
              onClick={() => setAddingRole('supplier')}
              className="mt-1.5 text-sm font-medium text-primary hover:underline"
            >
              + Add new supplier
            </button>
          </div>
          <div>
            <SelectField
              id="project-client"
              label="Client"
              value={values.clientUid}
              onChange={(event) => selectClient(event.target.value)}
            >
              <option value="">Select client…</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </SelectField>
            <button
              type="button"
              onClick={() => setAddingRole('client')}
              className="mt-1.5 text-sm font-medium text-primary hover:underline"
            >
              + Add new client
            </button>
          </div>
          {error && (
            <p role="alert" className="text-sm text-danger">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : project ? 'Save changes' : 'Create project'}
            </Button>
          </div>
        </form>
      </Modal>

      {addingRole && (
        <AddUserModal
          open
          lockedRole={addingRole}
          onClose={() => setAddingRole(null)}
          onCreated={(user) => {
            if (user.role === 'supplier') {
              setValues((current) => ({
                ...current,
                supplierUid: user.uid,
                supplierName: user.name,
              }))
            } else {
              setValues((current) => ({ ...current, clientUid: user.uid, clientName: user.name }))
            }
            setAddingRole(null)
          }}
        />
      )}
    </>
  )
}
